import React, { FunctionComponent, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import debounce from 'lodash.debounce';
import Column from '../../../../components/Column';
import Row from '../../../../components/Row';
import Text from '../../../../components/Text';
import InputNumber from '../../../../components/InputNumber';
import { styles } from '../../../../styles/themes/style';
import { useAccountActivityRequest } from '../../../../services/apis/accountActivity/useAccountActivityRequest';
import {
  VISIBLE_ACCOUNT_LIMIT_META,
  IAccountActivityStatus,
  IAccountLimitMeta,
  AccountLimitType,
} from '../../../../interfaces/models/accountActivity';
import { useAuthStore } from '../../../../stores/auth/useAuthStore';
import { AppRoutes } from '../../../../interfaces/store/appStore';
import { useNavigate } from 'react-router-dom';

const AccountActivityLimitsPanel: FunctionComponent = () => {
  const { getAccountActivity, updateAccountActivity } = useAccountActivityRequest();
  const getAccountActivityRef = useRef(getAccountActivity);
  const updateAccountActivityRef = useRef(updateAccountActivity);
  getAccountActivityRef.current = getAccountActivity;
  updateAccountActivityRef.current = updateAccountActivity;

  const { store: { disablePremiumFeatures } } = useAuthStore();
  const navigate = useNavigate();

  const [status, setStatus] = useState<IAccountActivityStatus | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const data = await getAccountActivityRef.current();
      if (cancelled) return;
      if (data) setStatus(data as IAccountActivityStatus);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  const savePatch = useMemo(
    () => debounce(async (patch: Parameters<typeof updateAccountActivity>[0]) => {
      const data = await updateAccountActivityRef.current(patch);
      if (data) setStatus(data as IAccountActivityStatus);
    }, 600),
    [],
  );

  useEffect(() => () => savePatch.cancel(), [savePatch]);

  const onLimitChange = (key: AccountLimitType, value: number) => {
    if (!status) return;
    setStatus({
      ...status,
      dailyLimits: { ...status.dailyLimits, [key]: value },
    });
    void savePatch({ dailyLimits: { [key]: value } });
  };

  if (loading) {
    return <Text $paragraph2 $lighter>Loading account activity…</Text>;
  }

  if (!status?.featureEnabled) {
    return (
      <UpgradeBox>
        <Text $paragraph2>
          {status?.businessOnly
            ? 'Daily sending limits are available on the Growth plan. Upgrade to configure per-action limits for your LinkedIn profile.'
            : 'Account activity limits are not enabled for your workspace.'}
        </Text>
        {disablePremiumFeatures && (
          <LinkBtn type="button" onClick={() => navigate(AppRoutes.SETTINGS_BILLING)}>
            View billing
          </LinkBtn>
        )}
      </UpgradeBox>
    );
  }

  return (
    <Column gap="20px">
      <Column gap="8px">
        <Text $paragraph2 $lighter>
          Daily limits apply to your LinkedIn profile across all campaigns.
          Open Profile and paid InMail are separate (defaults 40 each).
          Connection requests also have a hard weekly cap of {status.connectionRequestsWeekly?.limit ?? 150} per profile.
        </Text>
      </Column>

      <Section>
        <Row spaceBetween alignItems style={{ marginBottom: 16 }}>
          <Text $label $capitalize>Max daily quota</Text>
        </Row>
        <LimitGrid>
          {VISIBLE_ACCOUNT_LIMIT_META.map(({ key, label, hint }: IAccountLimitMeta) => {
            const max = status.maxDailyLimits[key] ?? 0;
            const used = status.usageToday[key] ?? 0;
            const limit = status.dailyLimits[key] ?? max;
            const exhausted = limit > 0 && used >= limit;
            const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
            return (
              <LimitCard key={key} $exhausted={exhausted}>
                <LimitCardHeader>
                  <Row alignItems gap="8px" style={{ minWidth: 0 }}>
                    <LimitDot $exhausted={exhausted} />
                    <Text $paragraph2 $bold>{label}</Text>
                  </Row>
                  {exhausted && <ExhaustedBadge>Limit reached</ExhaustedBadge>}
                </LimitCardHeader>
                <LimitHint>{hint}</LimitHint>
                <LimitControls>
                  <Row alignItems gap="12px">
                    <InputNumber
                      prefilledValue={limit}
                      minValue={0}
                      maxValue={max}
                      onChangeHandler={(v: number) => onLimitChange(key, v)}
                    />
                    <Text $paragraph3 $lighter>/ {max} max</Text>
                  </Row>
                  <UsageBar><UsageFill $exhausted={exhausted} style={{ width: `${pct}%` }} /></UsageBar>
                  <UsageText $exhausted={exhausted}>
                    {used} used today
                  </UsageText>
                </LimitControls>
              </LimitCard>
            );
          })}
        </LimitGrid>
      </Section>
    </Column>
  );
};

const Section = styled.div`
  padding: 20px;
  border: 1px solid #eef2f7;
  border-radius: 12px;
  background: ${styles.colors.white};
`;

const LimitGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 14px;
`;

const LimitCard = styled.div<{ $exhausted?: boolean }>`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 16px;
  border-radius: 10px;
  border: 1px solid ${p => p.$exhausted ? styles.colors.red200 : '#eef2f7'};
  background: ${p => p.$exhausted ? '#fef3f3' : styles.colors.white};
  transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
  &:hover {
    border-color: ${p => p.$exhausted ? styles.colors.red300 : '#dde3ee'};
    box-shadow: 0 4px 14px rgba(20,30,80,0.06);
    transform: translateY(-1px);
  }
`;

const LimitDot = styled.span<{ $exhausted?: boolean }>`
  flex-shrink: 0;
  width: 7px; height: 7px; border-radius: 50%;
  background: ${p => p.$exhausted ? styles.colors.red500 : styles.colors.primary600};
`;

const LimitCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 4px;
  min-height: 22px;
  flex-shrink: 0;
`;

const LimitHint = styled(Text).attrs({ $paragraph3: true, $lighter: true })`
  display: block;
  flex: 1 1 auto;
  margin-bottom: 12px;
  line-height: 1.45;
`;

const LimitControls = styled.div`
  margin-top: auto;
  flex-shrink: 0;
`;

const ExhaustedBadge = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: ${styles.colors.red500};
  white-space: nowrap;
`;

const UsageBar = styled.div`
  margin-top: 12px;
  width: 100%;
  height: 5px;
  border-radius: 99px;
  background: #eef1f7;
  overflow: hidden;
`;

const UsageFill = styled.div<{ $exhausted?: boolean }>`
  height: 100%;
  border-radius: 99px;
  background: ${p => p.$exhausted ? styles.colors.red500 : styles.colors.primary600};
  transition: width 0.3s ease;
`;

const UsageText = styled(Text).attrs({ $paragraph3: true })<{ $exhausted?: boolean }>`
  margin-top: 7px;
  color: ${p => p.$exhausted ? styles.colors.red500 : styles.colors.black300};
`;

const UpgradeBox = styled.div`
  padding: 20px;
  border-radius: 8px;
  border: 1px solid ${styles.colors.black100};
  background: ${styles.colors.black100};
`;

const LinkBtn = styled.button`
  margin-top: 12px;
  background: none;
  border: none;
  padding: 0;
  color: ${styles.colors.primary600};
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;
`;

export default AccountActivityLimitsPanel;
